import { Component, OnInit, OnDestroy, inject, signal, computed, WritableSignal } from '@angular/core';
// import { CommonModule } from '@angular/common'; QUEDA PARA CUANDO SE VUELVAN STANDALONE
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, /*ReactiveFormsModule,*/ Validators } from '@angular/forms'; // COMENTARIO QUEDA PARA CUANDO SE VUELVAN STANDALONE
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, firstValueFrom, map, Observable, of, Subscription, switchMap } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
// Si usamos ngx-mask para el documento: QUEDA PARA CUANDO SE VUELVAN STANDALONE
// import { NgxMaskDirective } from 'ngx-mask';
// Bootstrap modal (opcional si usás el modal de Bootstrap)
import * as bootstrap from 'bootstrap';
import { LoadingService } from 'src/app/services/loading.service';
import { AuthService } from 'src/app/services/auth.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { TipoDocumento } from 'src/app/models/TipoDocumento';
import { API_URI } from 'src/environments/environment';

@Component({
    selector: 'app-my-account-identidad',
    templateUrl: './my-account-identidad.component.html',
    styleUrls: ['./my-account-identidad.component.css'],
    standalone: false
})
export class MyAccountIdentidadComponent implements OnInit, OnDestroy {
  private _authService = inject(AuthService);
  private _router = inject(Router);
  private _location = inject(Location);
  private _fb = inject(FormBuilder);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private sanitizer = inject(DomSanitizer);

  private subs = new Subscription();

  // ------- estado -------
  error = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());
  asociadoCardio = signal<boolean>(false);
  maskDocumento = signal<string>('00000009'); // DNI por defecto
  isCheckingDni = signal<boolean>(false);   // spinner local
  private asociadoPorDni = false;
  maskSocio = signal<string>('00000000'); // 8 caracteres
  isCheckingSocio = signal<boolean>(false);   // spinner local
  private asociadoPorSocio = false;

  // señales derivadas desde el servicio
  tiposDocs = computed<TipoDocumento[]>(() => {
    const base = this._usuarioService.tiposDocumentos() ?? [];
    return [{ id: 0, x_descripcion: 'Seleccione', b_activo: true }, ...base];
  });

  // imágenes y archivos
  x_img_avatar?: File;
  x_img_dni_frente?: File;
  x_img_dni_dorso?: File;

  preview_img_avatar = signal<string>('');
  preview_img_avatar_large = signal<string>('');
  preview_img_dni_frente = signal<string>('');
  preview_img_dni_dorso = signal<string>('');

  private modalInstance?: any;

  // Form
  form = this._fb.group({
    nombre: ['', Validators.required],
    apellido: ['', Validators.required],
    telefono: ['', [Validators.required, Validators.pattern(/^[0-9]*$/)]],
    tipo_documento: [0, Validators.required], // 0 = Seleccione
    nro_documento: ['', [Validators.required, Validators.minLength(7), Validators.maxLength(13)]],
    telefono_emergencia: ['', Validators.pattern(/^[0-9]*$/)], // requerido si es asociadoCardio(),
    nro_socio: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(8)]]
  });

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // Si necesitás (re)cargar usuario/cliente desde backend, hacelo bajo el loader:
    await this._loadingService.run(async () => {
      try {
        // Asegura bootstrap de sesión (idempotente, ok en web/nativo)
        await this._authService.ensureSession();
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this._router.navigate(['/sign-in']);
      }
    });

    // Cargar valores iniciales desde el servicio
    const cli = this._usuarioService.cliente();
    const usu = this._usuarioService.usuario();

    if (!cli?.id || !usu?.id) {
      this.error.set('No se pudo cargar tu cuenta. Volvé a intentar.');
      this._router.navigate(['/sign-in']);
      return;
    }

    // Importante: fijamos tipo_documento y la máscara ANTES de cargar
    // nro_documento. El cambio de [mask] recién llega al input en el
    // próximo ciclo de detección de cambios de Angular (no es sincrónico
    // con el .set() del signal) — por eso el patchValue de nro_documento
    // va en un setTimeout: si se hace en el mismo tick, ngx-mask todavía
    // tiene puesta la máscara vieja de DNI (8 dígitos) y trunca un
    // CUIT/CUIL (11 dígitos), aunque en la base el dato esté completo.
    this.form.patchValue({
      nombre: cli.x_nombre ?? '',
      apellido: cli.x_apellido ?? '',
      telefono: cli.x_telefono ?? '',
      tipo_documento: usu.id_tipo_documento ?? 0,
    });
    this.actualizarMascara();
    setTimeout(() => {
      this.form.patchValue({
        nro_documento: usu.x_nro_documento ?? '',
        telefono_emergencia: usu.x_telefono_emergencia ?? '',
        nro_socio: usu.x_nro_socio ?? '',
      });
    });

    // Previews
    if (usu.x_img_avatar) {
      this.preview_img_avatar.set(`${API_URI}uploads/images/small-${usu.x_img_avatar}`);
      this.preview_img_avatar_large.set(`${API_URI}uploads/images/large-${usu.x_img_avatar}`);
    }
    if (cli.x_img_dni_frente) {
      this.preview_img_dni_frente.set(`${API_URI}uploads/images/${cli.x_img_dni_frente}`);
    }
    if (cli.x_img_dni_dorso) {
      this.preview_img_dni_dorso.set(`${API_URI}uploads/images/${cli.x_img_dni_dorso}`);
    }

    // escuchar cambios de tipo_documento para ajustar máscara
    const tipoCtrl = this.form.get('tipo_documento')!;
    this.subs.add(
      tipoCtrl.valueChanges.subscribe(() => this.actualizarMascara())
    );

    // Suscripción al cambio de DNI con debounce + spinner local
    const dniCtrl = this.form.get('nro_documento')!;
    const dniSub = dniCtrl.valueChanges.pipe(
      map(v => (v ?? '').toString()),
      map(v => v.replace(/\D/g, '')), // solo dígitos para consultar
      filter(v => v.length >= 6),      // umbral mínimo antes de consultar
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(dni => {
        this.isCheckingDni.set(true);
        return this._usuarioService.checkAsociadoXDni(dni).pipe(
          catchError(() => of({ habilitado: false } as any)),
          finalize(() => this.isCheckingDni.set(false))
        );
      })
    ).subscribe(res => {
      this.asociadoPorDni = !!res?.habilitado;
      this.asociadoCardio.set(this.asociadoPorDni || this.asociadoPorSocio);
      this.actualizarValidacionTelefonoEmergencia();
    });

    this.subs.add(dniSub);

    // Si hay DNI cargado, disparo una verificación inicial
    if (usu.x_nro_documento) {
      dniCtrl.setValue(usu.x_nro_documento, { emitEvent: true });
    }

    // Suscripción al cambio de Nro. Socio con debounce + spinner local
    const socioCtrl = this.form.get('nro_socio')!;
    const socioSub = socioCtrl.valueChanges.pipe(
      map(v => (v ?? '').toString()),
      map(v => v.replace(/\D/g, '')), // solo dígitos para consultar
      filter(v => v.length >= 4),      // umbral mínimo antes de consultar
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(nroSocio => {
        this.isCheckingSocio.set(true);
        return this._usuarioService.checkAsociadoXNroSocio(nroSocio).pipe(
          catchError(() => of({ habilitado: false } as any)),
          finalize(() => this.isCheckingSocio.set(false))
        );
      })
    ).subscribe(res => {
      this.asociadoPorSocio = !!res?.habilitado;
      this.asociadoCardio.set(this.asociadoPorDni || this.asociadoPorSocio);
      this.actualizarValidacionTelefonoEmergencia();
    });

    this.subs.add(socioSub);

    // Si hay Nro. Socio cargado, disparo una verificación inicial
    if (usu.x_nro_socio) {
      socioCtrl.setValue(usu.x_nro_socio, { emitEvent: true });
    }

    // Cerrar modal si el usuario navega hacia atrás
    this._location.subscribe(() => {
      const modalEl = document.getElementById('modalPreviewImg');
      if (modalEl && this.modalInstance) {
        this.modalInstance.hide();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.modalInstance) this.modalInstance.hide();
    if (this.modalConfirmarEliminarInstance) this.modalConfirmarEliminarInstance.hide();
    this.subs.unsubscribe();
  }

  back() {
    this._location.back();
  }

  // -------- Modal foto perfil --------
  openModal() {
    const modalEl = document.getElementById('modalPreviewImg');
    if (modalEl) {
      this.modalInstance = new (bootstrap as any).Modal(modalEl);
      this.modalInstance.show();
    }
  }

  // -------- Máscara según tipo documento --------
  actualizarMascara() {
    const tipo = this.form.value.tipo_documento;
    if (tipo === 1 || tipo === 2) { // 1 - CUIT / 2 - CUIL
      this.maskDocumento.set('00-00000000-0'); // CUIT / CUIL con formato 99-99999999-9
    } else {
      // DNI: permite 7 u 8 dígitos
      this.maskDocumento.set('00000009');
    }
  }

  private actualizarValidacionTelefonoEmergencia() {
    // Condicionalmente agregar o eliminar validación para teléfono de emergencia
    // (el patrón de solo-números se mantiene siempre)
    const ctrl = this.form.get('telefono_emergencia');
    if (!ctrl) return;
    const validators = this.asociadoCardio()
      ? [Validators.required, Validators.pattern(/^[0-9]*$/)]
      : [Validators.pattern(/^[0-9]*$/)];
    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity();
  }

  // -------- Solo números en teléfono / contacto de emergencia --------
  soloNumeros(controlName: 'telefono' | 'telefono_emergencia', event: Event) {
    const input = event.target as HTMLInputElement;
    const filtrado = input.value.replace(/\D/g, '');
    if (filtrado !== input.value) {
      this.form.get(controlName)?.setValue(filtrado);
    }
  }

  // -------- Guardar --------
  async saveUsuario() {
    this.error.set('');

    // Validaciones "a mano"
    const v = this.form.value;
    if (!v.apellido) { this.error.set('Debe Completar el Apellido'); return; }
    if (!v.nombre) { this.error.set('Debe Completar el Nombre'); return; }
    if (!v.telefono) { this.error.set('Debe Completar el Teléfono'); return; }
    if (!/^[0-9]*$/.test(v.telefono)) { this.error.set('El Teléfono debe contener solo números'); return; }
    if (!v.nro_documento) { this.error.set('Debe Completar el Nro. de Documento'); return; }
    if ((v.nro_documento ?? '').length > 13) { this.error.set('El número de documento no puede tener más de 13 caracteres'); return; }
    if ((v.nro_documento ?? '').length < 7) { this.error.set('El número de documento no puede tener menos de 7 caracteres'); return; }
    if (!v.tipo_documento || v.tipo_documento === 0) { this.error.set('Debe Seleccionar el Tipo de Documento'); return; }
    if (this.asociadoCardio() && !v.telefono_emergencia) { this.error.set('Debe Completar el Teléfono de Contacto de Emergencia'); return; }
    if (v.telefono_emergencia && !/^[0-9]*$/.test(v.telefono_emergencia)) { this.error.set('El Contacto de Emergencia debe contener solo números'); return; }
    if (!v.nro_socio) { this.error.set('Debe Completar el Nro. de Socio'); return; }
    if ((v.nro_socio ?? '').length > 8) { this.error.set('El número de socio no puede tener más de 8 caracteres'); return; }
    if ((v.nro_socio ?? '').length < 4) { this.error.set('El número de socio no puede tener menos de 4 caracteres'); return; }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.error.set('Por favor, completá los campos requeridos.');
      return;
    }

    await this._loadingService.run(async () => {
      // clonar y actualizar signal local (reactivo inmediato en UI)
      const cli = { ...(this._usuarioService.cliente() ?? {}) } as any;
      cli.x_apellido = v.apellido!;
      cli.x_nombre = v.nombre!;
      cli.x_telefono = v.telefono!;
      this._usuarioService.cliente.set(cli);

      const usu = { ...(this._usuarioService.usuario() ?? {}) } as any;
      usu.id_tipo_documento = v.tipo_documento!;
      usu.x_nro_documento = v.nro_documento!;
      usu.x_telefono_emergencia = v.telefono_emergencia || null;
      usu.x_nro_socio = v.nro_socio || null;
      this._usuarioService.usuario.set(usu);

      // Subidas de imágenes (si corresponde)
      // 1) Avatar
      if (this.x_img_avatar) {
        const res: any = await firstValueFrom(
          this._usuarioService.updateUsuarioImgAvatar(this.x_img_avatar, this._usuarioService.usuario().x_email!)
        );
        const u = { ...this._usuarioService.usuario() };
        u.x_img_avatar = res?.imagen ?? u.x_img_avatar;
        this._usuarioService.usuario.set(u);
      }

      // 2) DNI frente
      if (this.x_img_dni_frente) {
        const res: any = await firstValueFrom(
          this._usuarioService.updateClienteImgDniFrente(this.x_img_dni_frente, this._usuarioService.cliente().id!)
        );
        const c = { ...this._usuarioService.cliente() };
        c.x_img_dni_frente = res?.imagen ?? c.x_img_dni_frente;
        this._usuarioService.cliente.set(c);
      }

      // 3) DNI dorso
      if (this.x_img_dni_dorso) {
        const res: any = await firstValueFrom(
          this._usuarioService.updateClienteImgDniDorso(this.x_img_dni_dorso, this._usuarioService.cliente().id!)
        );
        const c2 = { ...this._usuarioService.cliente() };
        c2.x_img_dni_dorso = res?.imagen ?? c2.x_img_dni_dorso;
        this._usuarioService.cliente.set(c2);
      }

      // 4) Actualizar datos del cliente y usuario
      await firstValueFrom(this._usuarioService.updateCliente(this._usuarioService.cliente()));
      await firstValueFrom(this._usuarioService.updateUsuario(this._usuarioService.usuario()));

      // 5) Refrescamos si es socio o no
      await this._usuarioService.refreshAsociadoCardio();

      // 6) Navegar
      await this._router.navigate(['/my-account']);
    }).catch((e) => {
      console.error(e);
      this.error.set('Error al actualizar el usuario');
    });
  }

  // -------- Handlers de archivos y previews --------
  private async pickPhoto(fileName: string): Promise<{ file: File; dataUrl: string }> {
    // En web conviene Photos; en nativo Prompt (permite Cámara o Galería)
    const source = Capacitor.getPlatform() === 'web' ? CameraSource.Photos : CameraSource.Prompt;

    const photo: Photo = await Camera.getPhoto({
      source,
      // URI es más eficiente en memoria; luego lo convertimos a blob->File
      resultType: CameraResultType.Uri,
      quality: 85,
      allowEditing: false,
      correctOrientation: true,
      promptLabelHeader: 'Seleccionar imagen',
      promptLabelPhoto: 'Galería',
      promptLabelPicture: 'Cámara',
    });

    // Asegurar un webPath o dataUrl/base64
    const file = await this.photoToFile(photo, fileName);
    const dataUrl = await this.fileToDataUrl(file);
    return { file, dataUrl };
  }

  /** Convierte Photo de Capacitor a File */
  private async photoToFile(photo: Photo, fileName: string): Promise<File> {
    let blob: Blob | null = null;

    if (photo.webPath) {
      const res = await fetch(photo.webPath);
      blob = await res.blob();
    } else if (photo.dataUrl) {
      const res = await fetch(photo.dataUrl);
      blob = await res.blob();
    } else if (photo.base64String) {
      const byteString = atob(photo.base64String);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      blob = new Blob([ab], { type: 'image/jpeg' });
    }

    if (!blob) throw new Error('No se pudo leer la imagen.');
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  }

  /** Convierte File a DataURL (para preview) */
  private async fileToDataUrl(file: File): Promise<string> {
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });
  }

  // ==== Handlers de cada imagen usando los helpers ====

  async capturarFileImgAvatar(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('avatar.jpg');
      this.x_img_avatar = file;
      this.preview_img_avatar.set(dataUrl);
      this.preview_img_avatar_large.set(dataUrl);
    } catch (err) {
      console.error('Error al capturar avatar:', err);
    }
  }

  async capturarFileImgDniFrente(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('dni-frente.jpg');
      this.x_img_dni_frente = file;
      this.preview_img_dni_frente.set(dataUrl);
    } catch (err) {
      console.error('Error al capturar DNI frente:', err);
    }
  }

  async capturarFileImgDniDorso(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('dni-dorso.jpg');
      this.x_img_dni_dorso = file;
      this.preview_img_dni_dorso.set(dataUrl);
    } catch (err) {
      console.error('Error al capturar DNI dorso:', err);
    }
  }

  // ------------------ Eliminar imágenes ------------------

  nombreImagenAEliminar = signal<string>('');
  private modalConfirmarEliminarInstance?: any;
  private accionEliminarPendiente: (() => Promise<void>) | null = null;

  private abrirConfirmarEliminarImagen(nombre: string, accion: () => Promise<void>): void {
    this.nombreImagenAEliminar.set(nombre);
    this.accionEliminarPendiente = accion;
    const modalEl = document.getElementById('modalConfirmarEliminarImagen');
    if (modalEl) {
      this.modalConfirmarEliminarInstance = new (bootstrap as any).Modal(modalEl);
      this.modalConfirmarEliminarInstance.show();
    }
  }

  async confirmarEliminarImagen(): Promise<void> {
    const accion = this.accionEliminarPendiente;
    this.accionEliminarPendiente = null;
    this.modalConfirmarEliminarInstance?.hide();
    if (accion) await accion();
  }

  private async ejecutarEliminarImagen(
    borrar: (id: string) => Observable<any>,
    previewSignal: WritableSignal<string>,
    campo: 'x_img_dni_frente' | 'x_img_dni_dorso',
    limpiarArchivo: () => void
  ): Promise<void> {
    await this._loadingService.run(async () => {
      try {
        const clienteId = this._usuarioService.cliente().id!;
        await firstValueFrom(borrar(clienteId));
        const c = { ...(this._usuarioService.cliente() as any) };
        c[campo] = null;
        this._usuarioService.cliente.set(c);
        previewSignal.set('');
        limpiarArchivo();
      } catch (e) {
        console.error('Error al eliminar la imagen:', e);
        this.error.set('No se pudo eliminar la imagen. Intentá nuevamente.');
      }
    });
  }

  eliminarImgDniFrente(): void {
    this.abrirConfirmarEliminarImagen('el DNI frente', () =>
      this.ejecutarEliminarImagen(
        (id) => this._usuarioService.deleteClienteImgDniFrente(id),
        this.preview_img_dni_frente,
        'x_img_dni_frente',
        () => this.x_img_dni_frente = undefined
      )
    );
  }

  eliminarImgDniDorso(): void {
    this.abrirConfirmarEliminarImagen('el DNI dorso', () =>
      this.ejecutarEliminarImagen(
        (id) => this._usuarioService.deleteClienteImgDniDorso(id),
        this.preview_img_dni_dorso,
        'x_img_dni_dorso',
        () => this.x_img_dni_dorso = undefined
      )
    );
  }

  extraerBase64 = async ($event: any) => new Promise((resolve, reject) => {
    try {
      const unsafeImg = window.URL.createObjectURL($event);
      const image = this.sanitizer.bypassSecurityTrustUrl(unsafeImg);
      const reader = new FileReader();
      reader.readAsDataURL($event);
      reader.onload = () => {
        resolve({
          base: reader.result
        });
      };
      reader.onerror = error => {
        resolve({
          base: null
        });
      };
      return;
    } catch (e) {
      return null;
    }
  })
}